import React from 'react';
import { Autocomplete, Box, Checkbox, Chip, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { ROLE_DEFINITIONS } from '../../../../services/roleService';
import { VIEW_AS_ROLE_ORDER } from '../../../../services/viewAs';
import { autoDisplayName, unitForRole, unitOptions } from './memberForm';

/**
 * The member fields (Beta-1 plan P4-09 / P4-13 / D07.1): First name · Last name · Display name
 * (auto "First Last", editable) · Email · Title/Position · Role · Site / Department / NWT
 * (multi-select where the role allows several units) · Notify by email (Add only).
 * Controlled: `form` + `onChange(patch)`; `errors` from validateMemberForm.
 */
export function MemberFormFields({ form, onChange, errors = {}, organization, mode = 'add', disabled = false }) {
  const opts = unitOptions(organization);
  const unit = unitForRole(form.role);
  const set = (patch) => onChange(patch);
  const setName = (key, value) => {
    const next = { [key]: value };
    const auto = autoDisplayName(form.firstName, form.lastName);
    if (!form.displayName || form.displayName === auto) {
      next.displayName = autoDisplayName(key === 'firstName' ? value : form.firstName, key === 'lastName' ? value : form.lastName);
    }
    set(next);
  };
  const multi = (label, key, list, helper) => (
    <Autocomplete
      multiple
      size="small"
      options={list}
      disableCloseOnSelect
      disabled={disabled}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={list.filter((o) => (form[key] || []).includes(o.id))}
      onChange={(_, value) => set({ [key]: value.map((o) => o.id) })}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0 }} />
          {option.label}
        </li>
      )}
      renderTags={(value, getTagProps) => value.map((option, index) => <Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />)}
      renderInput={(params) => <TextField {...params} margin="dense" label={label} required error={Boolean(errors[key])} helperText={errors[key] || helper} />}
    />
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 1 }}>
        <TextField margin="dense" label="First name" fullWidth required value={form.firstName} onChange={(e) => setName('firstName', e.target.value)} error={Boolean(errors.firstName)} helperText={errors.firstName} disabled={disabled} />
        <TextField margin="dense" label="Last name" fullWidth required value={form.lastName} onChange={(e) => setName('lastName', e.target.value)} error={Boolean(errors.lastName)} helperText={errors.lastName} disabled={disabled} />
      </Stack>
      <TextField margin="dense" label="Display name" fullWidth value={form.displayName} onChange={(e) => set({ displayName: e.target.value })} helperText="How the name appears in the app (defaults to First Last)" disabled={disabled} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 1 }}>
        <TextField margin="dense" label="Email" type="email" fullWidth required={mode === 'add'} value={form.email} onChange={(e) => set({ email: e.target.value })} error={Boolean(errors.email)} helperText={errors.email} disabled={disabled || mode === 'edit'} />
        <TextField margin="dense" label="Title / position" fullWidth value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Shift Supervisor" disabled={disabled} />
      </Stack>
      <TextField
        margin="dense" label="Role (access level)" fullWidth required select SelectProps={{ native: true }}
        value={form.role} onChange={(e) => set({ role: e.target.value })} error={Boolean(errors.role)} helperText={ROLE_DEFINITIONS[form.role]?.description || errors.role} disabled={disabled}
      >
        {VIEW_AS_ROLE_ORDER.map((key) => (<option key={key} value={key}>{ROLE_DEFINITIONS[key]?.label || key}</option>))}
      </TextField>

      {unit === 'site' && multi('Site(s)', 'siteIds', opts.sites, 'A site manager over several sites: pick each one.')}
      {unit === 'department' && multi('Department(s)', 'deptIds', opts.departments, 'A department head over two departments: pick both.')}
      {unit === 'team' && multi(form.role === 'respondent' ? 'Natural Work Team (NWT)' : 'Natural Work Team(s)', 'nwtIds', opts.teams, form.role === 'respondent' ? 'Required — their scores, surveys and OE Conditions are scoped to this NWT.' : 'A supervisor over more than one crew: pick each NWT.')}
      {unit === null && (
        <>
          <Autocomplete
            size="small"
            options={opts.teams}
            disabled={disabled}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={opts.teams.find((o) => (form.nwtIds || [])[0] === o.id) || null}
            onChange={(_, value) => set({ nwtIds: value ? [value.id] : [] })}
            renderInput={(params) => <TextField {...params} margin="dense" label="Home NWT (optional)" helperText="Organization-wide role — no site or department needed. Optionally name the team they sit in (e.g. Executive Team) so they appear there and can take its surveys." />}
          />
        </>
      )}
      {/* Tim 26 Oct (D08 r13): the roles he asked about are these roles with several scopes */}
      {(unit === 'site' || unit === 'department') && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {unit === 'site'
            ? 'A Regional Manager is a Facility/Site/Plant Manager with several sites selected above.'
            : 'A Department Head over several departments, or a Coordinator across them, simply selects each department above.'}
        </Typography>
      )}

      {mode === 'add' && (
        <FormControlLabel
          sx={{ mt: 1.5, alignItems: 'flex-start' }}
          control={<Switch checked={form.notify !== false} onChange={(e) => set({ notify: e.target.checked })} disabled={disabled} />}
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>{form.notify !== false ? 'Notify by email' : 'Quiet: add without emailing'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Welcome email is notification-only — nothing for the member to acknowledge. Recommended: the CEO sends a heads-up email first, then add with Notify on. Quiet adds show you the temporary password to hand over yourself.
              </Typography>
            </Box>
          }
        />
      )}
    </Box>
  );
}

export default MemberFormFields;
